import { cleanLink } from "../../util/helper";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import OutsideAlerter from "../utilities/useOutSideComponentClick";

import ROUTES from "../../constants/routes";
import { settingsPath } from "../../constants/routes";

import { eventEmitter } from "../../eventEmitters/EventEmitters";
import {
  enterPlaybackMode,
  exitPlaybackMode,
  mode as playbackMode,
} from "../../util/playbackModeService";
import { getEntitlements } from "../../util/entitlements";
import { loadAwayPlace } from "../../util/playbackResume";

import AccountIssueBanner from "./AccountIssueBanner";
import OverageBanner from "./OverageBanner";
import PaymentFailedBanner from "./PaymentFailedBanner";
import Logo from "../../assets/images/Skydive_logo_beta.svg";
import SettingsIcon from "../../assets/images/settings.png";
import HelpIcon from "../../assets/images/help.png";
import HeadphonesIcon from "../../assets/images/headphones-icon.svg";
import HelpDropdownIcon from "../../assets/images/help-icon-dropdown.svg";
import "./Header.scss";
import { useTranslation } from "react-i18next";
import SiteSelector from "./SiteSelector";
import TrafficIndicator from "./TrafficIndicator";
import AmbientGreeting from "./AmbientGreeting";

export interface HeaderProps {
  // ConversionSetup (the pre-verification "install your tracking code"
  // page) renders this header at a URL with no site-id segment
  // (/conversion-setup, not /conversion-setup/:id) - every nav link below
  // (Settings especially) derives its target by parsing the LAST segment
  // of window.location.pathname assuming it's always preceded by a site
  // id. On this page that assumption is false, so Settings ends up
  // navigating to a URL with the literal string "conversion-setup" in
  // place of a site id - a real, reported bug ("clicking Settings
  // breaks"). Rather than patch that fragile path-parsing (used in
  // multiple places here and easy to get subtly wrong again), this page
  // just doesn't get the interactive nav at all - matches the product
  // decision that these links don't belong on a page reachable before
  // onboarding/verification is even complete anyway. Logo and site name
  // still render for branding/context.
  hideNav?: boolean;
}

// DEMO BUILD ONLY (REACT_APP_DEMO=1): the "Demo data" tag and "Simulate a campaign" button. Folded away in every other build.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const DemoControls: React.ComponentType = process.env.REACT_APP_DEMO === "1" ? require("../../demo/DemoControls").default : () => null;

const Header = ({ hideNav = false }: HeaderProps) => {
  const [setDropdown, showDropdown] = useState(false);
  const [isPlaybackMode, setIsPlaybackMode] = useState(playbackMode === "playback");
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const { t: translation } = useTranslation();
  const handleOutsideClick = () => {
    showDropdown(false);
  };

  useEffect(() => {
    const onModeChanged = ({ mode }: { mode: string }) => {
      setIsPlaybackMode(mode === "playback");
    };
    eventEmitter.addListener("modeChanged", onModeChanged);
    return () => {
      eventEmitter.removeListener("modeChanged", onModeChanged);
    };
  }, []);

  // Playback is plan-gated (see util/entitlements.ts's own comment) - a
  // Base Jump account keeps SEEING this nav link (visually locked, not
  // hidden entirely) so it reads as "here's what you'd get," not simply
  // absent. Recomputed on every render rather than memoized - accountRecord
  // itself only ever refreshes on a handful of specific events (login/
  // settings mount/subscription change), so this is already about as fresh
  // as it can be without a bigger refetch-triggering change.
  const { playbackAllowed } = getEntitlements(authReducer?.accountRecord);
  // Red-dot alert on the Settings nav item - currently only the raw-data
  // deletion warning (dataDeletionWarningStage flows straight through on
  // accountRecord, no extra RPC needed - getAccountRecord returns the whole
  // account document), but written as "does an issue exist" rather than
  // hardcoded to this one field so a future second issue type is a one-line
  // addition here, not a rewrite.
  const hasAccountIssue =
    authReducer?.accountRecord?.dataDeletionWarningStage != null;

  const handlePlaybackToggle = () => {
    if (!playbackAllowed) {
      return;
    }
    if (isPlaybackMode) {
      exitPlaybackMode();
    } else {
      // Back to the day the user left (their place on it is restored once it loads, in AnalyticsView); yesterday if they have not been here.
      enterPlaybackMode(loadAwayPlace()?.date);
    }
  };
  return (
    <>
    <header className="header">
      <Link to="https://www.theskydiveapp.com/">
        <img src={Logo} alt="logo" className="logo" />
      </Link>

      <div className="header-site">
      <h5 className="hover-tooltip">
        {window.location.pathname ===
        (window.location.pathname.split("/")[
          window.location.pathname.split("/").length - 1
        ] === "conversion-setup"
          ? ROUTES.CONVERSION_SETUP.replace(":id", "")
          : ROUTES.CONVERSION_SETUP.replace(
              ":id",
              window.location.pathname.split("/")[
                window.location.pathname.split("/").length - 1
              ]
            ))
          ? ""
          : <SiteSelector />}
        {window.location.pathname !==
          (window.location.pathname.split("/")[
            window.location.pathname.split("/").length - 1
          ] === "conversion-setup"
            ? ROUTES.CONVERSION_SETUP.replace(":id", "")
            : ROUTES.CONVERSION_SETUP.replace(
                ":id",
                window.location.pathname.split("/")[
                  window.location.pathname.split("/").length - 1
                ]
              )) && (
          <span className="tooltip-content tooltip-content-bottom header-tooltip">
            {cleanLink(authReducer.authData.site.host)}
          </span>
        )}
      </h5>
        {/*
          🔴 ANALYTICS VIEW ONLY (2026-09-28).
          TrafficIndicator reports THIS DASHBOARD'S OWN SOCKET - see its own
          header comment. That socket is created by AnalyticsView, so on any
          other route there is no socket to report on and the dot sat
          permanently ORANGE ("no signal") - most visibly on Settings, where
          Chris hit it.
          Correct about the page, useless to the reader, and actively harmful:
          a dot that is always orange somewhere trains people to ignore it on
          the one screen where orange means a real outage. Same failure as a
          deploy check applied out of context - the alarm stops carrying
          information.
          Matched on the path prefix, not the full route, because the customer
          route is "/analytics-view" while an admin's carries an :id segment
          (see routes.ts - the same trap that produced a no_route_match on the
          site switcher).
        */}
        {window.location.pathname.startsWith("/analytics-view") && (
          <TrafficIndicator />
        )}
      </div>

      {!hideNav && (
      <ul>
        {/* "Good afternoon, Chris" and a thin line, only on the dashboard (same rule as the traffic dot above):
            ambient life belongs to the live view. It stays the nav's FIRST item so Playback's own position
            never depends on it. See AmbientGreeting.tsx. */}
        {window.location.pathname.startsWith("/analytics-view") && <AmbientGreeting />}
        {process.env.REACT_APP_DEMO === "1" && <DemoControls />}
        <li
          onClick={handlePlaybackToggle}
          className={playbackAllowed ? "" : "hover-tooltip"}
        >
          <a
            className={`playback-toggle${isPlaybackMode ? " active" : ""}${
              playbackAllowed ? "" : " locked"
            }`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8v4l3 3m6-3a9 9 0 1 1-9-9 9 9 0 0 1 6.36 2.64L21 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 3v5h-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {translation("playback")}
            {!playbackAllowed && (
              <svg
                className="playback-lock-icon"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="11"
                  width="16"
                  height="10"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 11V7a4 4 0 0 1 8 0v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </a>
          {!playbackAllowed && (
            <span className="tooltip-content tooltip-content-bottom header-tooltip">
              Playback isn't available on your plan.{" "}
              <Link
                to={settingsPath()}
                className="upgrade-action"
              >
                {translation("upgrade_now")}
              </Link>
            </span>
          )}
        </li>
        <li>
          <Link
            to={
              window.location.pathname ===
              (window.location.pathname.split("/")[
                window.location.pathname.split("/").length - 1
              ] === "conversion-setup"
                ? ROUTES.CONVERSION_SETUP.replace(":id", "")
                : ROUTES.CONVERSION_SETUP.replace(
                    ":id",
                    window.location.pathname.split("/")[
                      window.location.pathname.split("/").length - 1
                    ]
                  ))
                ? ""
                : window.location.pathname.split("/")[
                    window.location.pathname.split("/").length - 1
                  ] === "analytics-view"
                ? settingsPath()
                : /*
                     Was appending the LAST PATH SEGMENT as an :id, which
                     produced "/settings/<siteId>" for a customer. That route
                     is only registered with an :id for a logged-in admin, so
                     the customer landed on a blank "no_route_match" page -
                     reported from both the playback date picker and the
                     historical date selector. settingsPath() builds the path
                     the same way PublicRoutes registers it.
                  */
                  settingsPath()
            }
          >
            <img src={SettingsIcon} alt="icon" />
           {translation("settings")}
           {hasAccountIssue && <span className="settings-alert-dot" />}
          </Link>
        </li>
        <OutsideAlerter onOutsideClick={handleOutsideClick}>
          <li onClick={() => showDropdown(!setDropdown)}>
            <a>
              <img src={HelpIcon} alt="icon" />
              {translation("help")}
            </a>
            {setDropdown ? (
              <div className="custom-dropdown">
                <div className="arrow-up" />
                <ul>
                  <li>
                    {/* A genuine external URL, not an internal route - React
                        Router's Link is meant for the latter, and its
                        behavior for an absolute external href isn't
                        guaranteed to actually open a new window/tab the way
                        target="_blank" on a real anchor does.
                        stopPropagation: this sits inside the parent "Help"
                        <li>'s own onClick (showDropdown toggle) - clicks
                        bubble by default, so clicking this link also fired
                        that handler, collapsing the dropdown (and
                        unmounting this very anchor) synchronously during
                        the same click that's supposed to trigger its
                        target="_blank" navigation. Stopping propagation
                        keeps this anchor's own default action the only
                        thing that happens on click. */}
                    <a
                      href="https://go.crisp.chat/chat/embed/?website_id=a14a2c2a-be82-4404-a9cd-6c115663c0b0"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src={HeadphonesIcon} alt="icon" />  {translation("live_chat")}
                    </a>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Link
                      to={ROUTES.HELP_INSTALLING_SKYDIVE}
                      target="_blank"
                    >
                      <img src={HelpDropdownIcon} alt="icon" />  {translation("help_articles")}
                    </Link>
                  </li>
                </ul>
              </div>
            ) : null}
          </li>
        </OutsideAlerter>


      </ul>
      )}

    </header>

      {/*
        THE system-message region: directly BELOW the site top bar, on every
        page. Lives inside Header so it renders in exactly ONE place and cannot
        drift between layouts - it was previously in the WithSideBarAndHeader
        route layout only, so Settings (the page a customer opens to FIX an
        over-limit or locked account) showed no system message at all.
      */}
      <AccountIssueBanner />
      <OverageBanner />
      {/*
        A ViewportBanner used to sit here, warning that a narrow window was
        showing a scaled-down view. REMOVED 2026-09-10 once the grid started
        reflowing its columns instead of scaling: every slot is now shown at
        full size at every width, so there was nothing left to warn about and
        its copy had become false on all three counts ("two per row", "past
        900px", "the full grid").

        Rule 4 is the reason it went rather than being reworded: a banner that
        is always true for a given viewer is one they learn to ignore, and
        that weakens the overage and payment notices sharing this region.
      */}
      {/*
        Last of the three, so a failed payment reads as the most urgent thing
        on screen when it coincides with another notice - it is the only one
        of them that ends in a suspended account.
      */}
      <PaymentFailedBanner />
    </>
  );
};

export default Header;
